"use client";
import { useEffect, useRef, useState } from "react";

type Props = {
  lines: string[];
  typewriterMs?: number;
  visibleLines?: number;
  className?: string;
};

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";

export function BattleLog({
  lines,
  typewriterMs = 28,
  visibleLines = 2,
  className,
}: Props) {
  const [charCount, setCharCount] = useState(0);
  const [typedIndex, setTypedIndex] = useState(-1);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevLenRef = useRef(0);

  // React to changes in lines length: new entry starts typewriter; shrink resets state.
  useEffect(() => {
    const prevLen = prevLenRef.current;
    const len = lines.length;

    if (len < prevLen) {
      // Lines shrank — reset typewriter state.
      setTypedIndex(-1);
      setCharCount(0);
    } else if (len > prevLen) {
      // New entry — type the newest line.
      setTypedIndex(len - 1);
      setCharCount(0);
    }

    prevLenRef.current = len;
  }, [lines.length]);

  // Advance typewriter char-by-char on the currently-typing line.
  useEffect(() => {
    if (typedIndex < 0 || typedIndex >= lines.length) return;
    const target = lines[typedIndex] ?? "";
    if (charCount >= target.length) return;

    const id = setTimeout(() => {
      setCharCount((c) => c + 1);
    }, typewriterMs);
    return () => clearTimeout(id);
  }, [charCount, typedIndex, lines, typewriterMs]);

  // Auto-scroll to bottom whenever the rendered content changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [charCount, typedIndex, lines.length]);

  const visible = lines.slice(Math.max(0, lines.length - visibleLines));
  const offset = Math.max(0, lines.length - visibleLines);

  const isTyping =
    typedIndex >= 0 &&
    typedIndex < lines.length &&
    charCount < (lines[typedIndex]?.length ?? 0);

  return (
    <>
      <style>{`
        @keyframes battlelog-blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        .battlelog-caret {
          display: inline-block;
          margin-left: 0.25rem;
          animation: battlelog-blink 0.6s steps(1) infinite;
        }
      `}</style>
      <div
        ref={scrollRef}
        className={className}
        role="log"
        aria-live="polite"
        style={{
          backgroundColor: CREAM,
          color: INK,
          border: `2px solid ${INK}`,
          padding: "0.625rem 0.75rem",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontWeight: 700,
          fontSize: "0.95rem",
          lineHeight: 1.35,
          imageRendering: "pixelated",
          overflowY: "auto",
          maxHeight: `${visibleLines * 1.6}em`,
        }}
      >
        {visible.map((line, i) => {
          const absoluteIndex = offset + i;
          let display = line;
          let showCaret = false;
          if (absoluteIndex === typedIndex && isTyping) {
            display = line.slice(0, charCount);
            showCaret = true;
          }
          return (
            <div key={absoluteIndex} style={{ whiteSpace: "pre-wrap" }}>
              {display}
              {showCaret && (
                <span className="battlelog-caret" aria-hidden>
                  {"\u25BC"}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
